import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  useParticipantIds,
  useScreenShare,
  useDailyEvent,
  useLocalSessionId,
} from '@daily-co/daily-react';
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import Avatar from "@mui/material/Avatar";
import Tooltip from "@mui/material/Tooltip";
import ClassifyIcon from '@mui/icons-material/Beenhere';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';

import './CallScreen.css';
import Tile from '../Tile/Tile';
import { Caption, CAPTION_CLEAR_DELAY_MS, CAPTION_STALE_DELAY_MS, captionLineFits, captionPage, parseCaption, tailCaption } from './captions';
import Tray from '../Tray/Tray';
import UserMediaError from '../UserMediaError/UserMediaError';
import { getChatSession } from '../../lib/api/Chat';
import { isNotFoundError } from '../../lib/api/http-errors';
import { useChat } from '../../lib/hooks/UseChat';
import { useTavusEventForwarding } from '../../lib/hooks/useTavusEventForwarding';
import { useUserInfoContext } from '../../lib/contexts/UserInfoContext';
import { useCustomThemeContext } from '../../lib/contexts/ThemeContext';
import { Persona } from '../../lib/api/Persona';
import { config } from '@config';
import { SubgraphModal } from '../Graph';
import { ClassificationBadge, SafetyRoute } from '../../components/ClassificationBadge/ClassificationBadge';
import { Sources } from '../../lib/models/Chat';

// Shared Components
import { MarkdownContent } from '../Chat/MarkdownContent';
import { FeedbackButtons } from '../Chat/FeedbackButtons';
import { stripExtraTags } from '@common/domain/tag-filter';
import type { FeedbackCategoryId } from '@common/domain/types';

// Citation File Viewer Imports
import { DocxViewer, urlContext, highlightTextContext, sourceContext, citationViewerOpenContext } from '../Chat/CitationViewer'
import { Source } from '../../lib/models/Chat';
import {
  switchChatSurface,
  trackChatSessionEnd,
  trackChatSessionStart,
  trackMessageSent,
} from '../../lib/analytics/chatAnalytics';

const POLL_INTERVAL_MS = 5000;

interface ConversationEntry {
  sources: Sources;
  exchangeId: string;
  question: string;
  response: string;
  safetyRoute?: SafetyRoute;
  seeds?: string[];
  retrievedChunkIds?: string[];
  usedChunkIds?: string[];
  metadata?: string;
  rating?: number;
  comment?: string;
  feedbackCategory?: FeedbackCategoryId[];
}

type TranscriptPanelTab = 'transcript' | 'events';

type TavusCviEvent = {
  id: string;
  receivedAtMs: number;
  fromId?: string;
  data: unknown;
};

function isPlainObject(value: unknown): value is Record<string, any> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function formatPerceptionToolCallEvent(raw: any) {
  const frames = Array.isArray(raw?.properties?.frames) ? raw.properties.frames : [];
  return {
    conversation_id: raw?.conversation_id,
    event_type: raw?.event_type,
    message_type: raw?.message_type,
    properties: {
      name: raw?.properties?.name,
      modality: raw?.properties?.modality,
      arguments: raw?.properties?.arguments,
      frames: frames.map((f: any) => ({ mime_type: f?.mime_type })),
      frames_count: frames.length,
    },
  };
}

function formatTavusEventForDisplay(raw: unknown): unknown {
  if (!isPlainObject(raw)) return raw;

  // Tavus CVI Perception Tool Call: frames include base64-encoded JPEG data. We only surface mime types.
  if (raw.event_type === 'conversation.perception_tool_call') {
    return formatPerceptionToolCallEvent(raw);
  }

  // Default: return raw event object as-is.
  return raw;
}

interface CallScreenProps {
  conversationId: string;
  leaveCall: () => void;
  persona: Persona | null;
  /** Text chat id when call was opened from /text-chat; keeps one GA session for text + video. */
  textChatSessionId?: string | null;
  layoutMode?: 'standalone' | 'learning-workspace';
  /** Lesson-opening text seeded as the first transcript entry, ahead of any real exchange. */
  openingPreamble?: string;
  onTeachingInteraction?: () => void;
  onTranscriptVisibilityChange?: (visible: boolean) => void;
}

const CAPTIONS_PREFERENCE_KEY = 'avatar:closed_captions_enabled';

function readCaptionsPreference(): boolean {
  try {
    return localStorage.getItem(CAPTIONS_PREFERENCE_KEY) !== 'false';
  } catch {
    return true;
  }
}

export default function CallScreen({
  conversationId,
  leaveCall,
  persona,
  textChatSessionId = null,
  layoutMode = 'standalone',
  openingPreamble,
  onTeachingInteraction,
  onTranscriptVisibilityChange,
}: CallScreenProps) {
  const [getUserMediaError, setGetUserMediaError] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  // Avatar mode leads with the avatar, not the transcript: closed on every
  // viewport. Reopened from the tray button, or the edge chevron on desktop.
  const [isTranscriptOpen, setIsTranscriptOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<TranscriptPanelTab>('transcript');
  const [eventsEnabled, setEventsEnabled] = useState(false);
  // The name the avatar speaks as (e.g. Giacomo), which can differ from the persona's listed name.
  // Read optionally: each portal's @config is its own object type, and only some set avatarSpeakerName.
  const portal = config as { avatarSpeakerName?: string; steadyCaptions?: boolean; avatarVideoFit?: 'cover' | 'contain'; avatarVideoBackground?: string };
  const speakerName = portal.avatarSpeakerName || persona?.name || 'Avatar';
  // Portals that opt in show steady two-line captions; the others keep the original tail caption.
  const steadyCaptions = portal.steadyCaptions === true;
  const remoteTileFit = { fit: portal.avatarVideoFit, fitBackground: portal.avatarVideoBackground };
  const [caption, setCaption] = useState<Caption | null>(null);
  const [captionsEnabled, setCaptionsEnabled] = useState(readCaptionsPreference);
  const toggleCaptions = useCallback(() => {
    setCaptionsEnabled((on) => {
      try { localStorage.setItem(CAPTIONS_PREFERENCE_KEY, String(!on)); } catch { /* storage unavailable: keep in-memory only */ }
      return !on;
    });
  }, []);
  const captionTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Steady captions wrap to the caption box as drawn (re-measured when it resizes).
  const [captionFit, setCaptionFit] = useState<((line: string) => boolean) | null>(null);
  const captionObserverRef = useRef<ResizeObserver | null>(null);
  const captionBoxRef = useCallback((box: HTMLDivElement | null) => {
    captionObserverRef.current?.disconnect();
    captionObserverRef.current = null;
    if (!box || typeof ResizeObserver === 'undefined') return;
    const measure = () => setCaptionFit(() => captionLineFits(box));
    measure();
    captionObserverRef.current = new ResizeObserver(measure);
    captionObserverRef.current.observe(box);
  }, []);
  useEffect(() => () => {
    if (captionTimerRef.current) clearTimeout(captionTimerRef.current);
  }, []);
  const [conversationEntries, setConversationEntries] = useState<ConversationEntry[]>([]);
  const [showClassification, setShowClassification] = useState(false);
  const [conversationError, setConversationError] = useState<string | null>(null);
  const [isConversationLoading, setIsConversationLoading] = useState(false);
  const [tavusEvents, setTavusEvents] = useState<TavusCviEvent[]>([]);
  
  const isMobile = () => window.innerWidth < 768;
  const getMinTranscriptWidth = () => (window.innerWidth < 768 ? window.innerWidth : window.innerWidth * 0.25);
  const getMaxTranscriptWidth = () => (window.innerWidth < 768 ? window.innerWidth : window.innerWidth * 0.50);
  
  const [transcriptWidth, setTranscriptWidth] = useState(() => 
    isMobile() ? window.innerWidth : Math.min(getMinTranscriptWidth(), getMaxTranscriptWidth())
  );
  const [draggedWidth, setDraggedWidth] = useState<number | null>(null);
  const [isResizing, setIsResizing] = useState(false);
  const dragStartX = React.useRef<number>(0);
  const dragStartWidth = React.useRef<number>(0);
  const { userInfo } = useUserInfoContext();
  const userInfoRef = useRef(userInfo);
  userInfoRef.current = userInfo;
  const { putRating } = useChat();
  const { isDarkMode } = useCustomThemeContext();
  const transcriptRef = useRef<HTMLDivElement>(null);
  const eventsRef = useRef<HTMLDivElement>(null);
  const shouldScrollTranscriptRef = useRef(true);
  const shouldScrollEventsRef = useRef(true);

  // Graph modal state
  const [graphModalOpen, setGraphModalOpen] = useState(false);
  const [graphModalSeeds, setGraphModalSeeds] = useState<string[]>([]);
  const [graphModalRetrieved, setGraphModalRetrieved] = useState<string[]>([]);
  const [graphModalUsed, setGraphModalUsed] = useState<string[]>([]);

  // need for citation file viewer
  const [url, setUrl] = useState("");
  const [highlightText, setHighlightText] = useState("");
  const [source, setSource] = useState<Source | null>(null);
  const [citationViewerOpen, setCitationViewerOpen] = useState(false);

  const videoGaSawLoadingRef = useRef(false);
  const videoGaBaselineSealedRef = useRef(false);
  const videoGaTrackedUserExchangeIdsRef = useRef<Set<string>>(new Set());

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    setIsResizing(true);
    dragStartX.current = e.clientX;
    dragStartWidth.current = transcriptWidth;
    setDraggedWidth(transcriptWidth);
  }, [transcriptWidth]);

  const handleMouseUp = useCallback(() => {
    if (draggedWidth !== null) {
      setTranscriptWidth(draggedWidth);
    }
    setIsResizing(false);
    setDraggedWidth(null);
  }, [draggedWidth]);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isResizing) return;
    const delta = dragStartX.current - e.clientX;
    const newWidth = dragStartWidth.current + delta;
    const clampedWidth = Math.min(Math.max(newWidth, getMinTranscriptWidth()), getMaxTranscriptWidth());
    setDraggedWidth(clampedWidth);
  }, [isResizing]);

  useEffect(() => {
    if (layoutMode === 'standalone' && isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      };
    }
  }, [isResizing, handleMouseMove, handleMouseUp, layoutMode]);

  useEffect(() => {
    if (layoutMode === 'standalone' || !isResizing) return;
    if (draggedWidth !== null) setTranscriptWidth(draggedWidth);
    setIsResizing(false);
    setDraggedWidth(null);
  }, [draggedWidth, isResizing, layoutMode]);

  useEffect(() => {
    videoGaSawLoadingRef.current = false;
    videoGaBaselineSealedRef.current = false;
    videoGaTrackedUserExchangeIdsRef.current.clear();
  }, [conversationId]);

  const continueTextChatSession = Boolean(textChatSessionId);

  useEffect(() => {
    if (!textChatSessionId) return;
    switchChatSurface(textChatSessionId, 'video');
    return () => {
      switchChatSurface(textChatSessionId, 'text');
    };
  }, [textChatSessionId]);

  useEffect(() => {
    if (!conversationId || continueTextChatSession) return;
    const id = conversationId;
    let started = false;
    const timer = window.setTimeout(() => {
      started = true;
      trackChatSessionStart(id, 'video');
    }, 0);

    return () => {
      window.clearTimeout(timer);
      if (started) {
        trackChatSessionEnd(id);
      }
    };
  }, [conversationId, continueTextChatSession]);

  useEffect(() => {
    if (!conversationId || !userInfoRef.current) return;

    if (isConversationLoading) {
      videoGaSawLoadingRef.current = true;
      return;
    }

    if (!videoGaBaselineSealedRef.current) {
      if (!videoGaSawLoadingRef.current) return;
      conversationEntries.forEach((e) => {
        if (e.question?.trim() && e.exchangeId) {
          videoGaTrackedUserExchangeIdsRef.current.add(e.exchangeId);
        }
      });
      videoGaBaselineSealedRef.current = true;
      return;
    }

    conversationEntries.forEach((e) => {
      if (!e.question?.trim() || !e.exchangeId) return;
      if (videoGaTrackedUserExchangeIdsRef.current.has(e.exchangeId)) return;
      videoGaTrackedUserExchangeIdsRef.current.add(e.exchangeId);
      trackMessageSent(textChatSessionId ?? conversationId, 'video');
    });
  }, [conversationId, conversationEntries, isConversationLoading, userInfo?.userId, textChatSessionId]);

  // Effect to track user scrolling to determine if we should auto-scroll
  useEffect(() => {
    const node = transcriptRef.current;
    if (!node) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = node;
      // Check if user is scrolled to the bottom (with a small tolerance for precision issues)
      if (scrollHeight - scrollTop <= clientHeight + 10) {
        shouldScrollTranscriptRef.current = true;
      } else {
        shouldScrollTranscriptRef.current = false;
      }
    };

    node.addEventListener('scroll', handleScroll);

    return () => {
      node.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Effect to scroll to bottom on new messages, but only if the user is already at the bottom
  useEffect(() => {
    if (shouldScrollTranscriptRef.current && transcriptRef.current) {
      transcriptRef.current.scrollTop = transcriptRef.current.scrollHeight;
    }
  }, [conversationEntries]);

  // Track scrolling for Events panel (mounted only when Events is active)
  useEffect(() => {
    const node = eventsRef.current;
    if (!node) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = node;
      if (scrollHeight - scrollTop <= clientHeight + 10) {
        shouldScrollEventsRef.current = true;
      } else {
        shouldScrollEventsRef.current = false;
      }
    };

    node.addEventListener('scroll', handleScroll);
    return () => node.removeEventListener('scroll', handleScroll);
  }, [activeTab]);

  useEffect(() => {
    if (shouldScrollEventsRef.current && eventsRef.current) {
      eventsRef.current.scrollTop = eventsRef.current.scrollHeight;
    }
  }, [tavusEvents, activeTab]);

  useDailyEvent('camera-error', useCallback(() => setGetUserMediaError(true), []));
  const forwardTavusEvent = useTavusEventForwarding(conversationId);

  // Tavus CVI emits interaction events via Daily "app-message". We capture and print them once the user
  // enables the Events panel, matching Tavus's `useObservableEvent` behavior from their docs.
  useDailyEvent('app-message' as any, useCallback((ev: any) => {
    const nextCaption = parseCaption(ev);
    if (nextCaption) {
      setCaption(nextCaption);
      if (captionTimerRef.current) clearTimeout(captionTimerRef.current);
      // Tavus may never send `final` (interruption, dropped message), so non-final captions expire too.
      captionTimerRef.current = setTimeout(
        () => setCaption(null),
        nextCaption.final ? CAPTION_CLEAR_DELAY_MS : CAPTION_STALE_DELAY_MS,
      );
    }
    const data = ev?.data ?? ev;
    forwardTavusEvent(data);
    if (!eventsEnabled) return;
    const next: TavusCviEvent = {
      id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
      receivedAtMs: Date.now(),
      fromId: ev?.fromId,
      data,
    };

    // Print events for debugging/visibility (as described in Tavus docs).
    // eslint-disable-next-line no-console
    console.log('[Tavus CVI event]', data);

    setTavusEvents((prev) => {
      const capped = prev.length > 499 ? prev.slice(prev.length - 499) : prev;
      return [...capped, next];
    });
  }, [eventsEnabled, forwardTavusEvent]));

  const { screens } = useScreenShare();
  const remoteParticipantIds = useParticipantIds({ filter: 'remote' });
  const localSessionId = useLocalSessionId();
  const isAlone = remoteParticipantIds.length < 1 && screens.length < 1;

  const toggleMinimize = () => setIsMinimized(!isMinimized);
  const setTranscriptVisibility = (visible: boolean) => {
    setIsTranscriptOpen(visible);
    onTranscriptVisibilityChange?.(visible);
  };
  const toggleTranscript = () => setTranscriptVisibility(!isTranscriptOpen);
  const onSelectTab = (tab: TranscriptPanelTab) => {
    setActiveTab(tab);
    if (tab === 'events') setEventsEnabled(true);
  };

  useEffect(() => {
    const currentUserInfo = userInfoRef.current;
    if (!conversationId || !currentUserInfo) {
      setConversationEntries([]);
      setConversationError(null);
      setIsConversationLoading(false);
      return;
    }

    let isMounted = true;
    let pollTimer: NodeJS.Timeout | null = null;
    let retryLoadingConversationCount = 6;

    const fetchConversation = async (withLoadingIndicator = false) => {
      const activeUserInfo = userInfoRef.current;
      if (!activeUserInfo) return;
      if (withLoadingIndicator) setIsConversationLoading(true);
      setConversationError(null);
      try {
        const session = await getChatSession(activeUserInfo, conversationId);
        const payload = session?.conversation || [];
        if (isMounted) {
          const mappedEntries = Array.isArray(payload) ? payload.map(entry => ({
            sources: entry.sources || {},
            exchangeId: entry.exchangeId,
            question: entry.question || '',
            response: entry.response || '',
            safetyRoute: entry.safetyRoute,
            seeds: entry.seeds,
            retrievedChunkIds: entry.retrievedChunkIds,
            usedChunkIds: entry.usedChunkIds,
            metadata: entry.metadata,
            rating: entry.rating,
            comment: entry.comment,
            feedbackCategory: entry.feedbackCategory,
          })) : [];

          // Only update state if the conversation has actually changed
          setConversationEntries(prevEntries => {
            if (JSON.stringify(prevEntries) !== JSON.stringify(mappedEntries)) {
              return mappedEntries;
            }
            return prevEntries;
          });
        }
      } catch (error) {
        if (isNotFoundError(error)) {
          if (isMounted) {
            setConversationEntries([]);
          }
        } else {
          console.error('[Conversation] Failed to load chat session', error);
          if (retryLoadingConversationCount > 0) {
            retryLoadingConversationCount -= 1;
          } else if (isMounted) {
            setConversationError('Unable to load conversation events.');
          }
        }
      } finally {
        if (isMounted) setIsConversationLoading(false);
      }
    };

    fetchConversation(true);
    pollTimer = setInterval(fetchConversation, POLL_INTERVAL_MS);
    return () => {
      isMounted = false;
      if (pollTimer) clearInterval(pollTimer);
    };
  }, [conversationId, userInfo?.userId]);

  const handleFeedback = async (
    exchangeId: string,
    kind: 'up' | 'down' | null,
    comment?: string,
    categories?: FeedbackCategoryId[],
  ) => {
    const activeUserInfo = userInfoRef.current;
    if (!activeUserInfo || !conversationId) return;
    const rating = kind === 'up' ? 1 : (kind === 'down' ? -1 : null);
    try {
      await putRating(conversationId, exchangeId, rating, comment, activeUserInfo.accessToken, persona?.id, categories);
      setConversationEntries(prev => prev.map(entry =>
        entry.exchangeId === exchangeId
          ? { ...entry, rating: rating ?? undefined, comment, feedbackCategory: categories ?? [] }
          : entry
      ));
    } catch (error) {
      console.error("Failed to submit feedback:", error);
    }
  };

  const renderCallScreen = () => {
    const isMobileLayout = isMobile();
    const isWorkspaceLayout = layoutMode === 'learning-workspace';
    return (
    <urlContext.Provider value={{ url, setUrl }}>
    <highlightTextContext.Provider value={{highlightText, setHighlightText}}>
    <sourceContext.Provider value={{source, setSource}}>
    <citationViewerOpenContext.Provider value={{citationViewerOpen, setCitationViewerOpen}}>
      <>
        <Box
          className={`call-layout${isWorkspaceLayout ? ' call-layout--learning-workspace' : ''} ${!isTranscriptOpen ? 'transcript-collapsed' : ''} ${isDarkMode ? 'dark' : 'light'}`}
          onPointerDown={isWorkspaceLayout ? () => onTeachingInteraction?.() : undefined}
          onKeyDown={isWorkspaceLayout ? () => onTeachingInteraction?.() : undefined}
          onWheel={isWorkspaceLayout ? () => onTeachingInteraction?.() : undefined}
          sx={{
            display: 'flex',
            flexDirection: isMobileLayout ? 'column' : 'row',
            width: '100%',
            height: '100%',
            position: 'relative',
            background: 'var(--color-background)',
          }}
        >
          <Box
            className={screens.length > 0 ? 'is-screenshare' : 'call'}
            sx={{
              position: 'relative',
              display: 'grid',
              gridTemplateColumns: isMobileLayout 
                ? '1fr'
                : 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: isMobileLayout ? '0.5rem' : '1rem',
              padding: isMobileLayout ? '0.5rem' : '1rem',
              width: isMobileLayout 
                ? '100%'
                : isWorkspaceLayout
                  ? undefined
                  : isTranscriptOpen ? `calc(100% - ${(draggedWidth !== null ? draggedWidth : transcriptWidth)}px)` : '100%',
              height: isMobileLayout && isTranscriptOpen 
                ? 'auto'
                : isMobileLayout || isWorkspaceLayout ? '100%' : 'auto',
              flex: isMobileLayout && !isTranscriptOpen ? 1 : undefined,
              transition: isWorkspaceLayout || isResizing ? 'none' : 'width 0.3s ease-in-out, height 0.3s ease-in-out',
              overflow: 'hidden',
            }}
          >
            {localSessionId && (
              <Tile
                id={localSessionId}
                isLocal
                isAlone={isAlone}
                isMinimized={isMinimized}
                toggleMinimize={toggleMinimize}
              />
            )}
            {remoteParticipantIds.length > 0 || screens.length > 0 ? (
              <>
                {remoteParticipantIds.map((id) => <Tile key={id} id={id} {...remoteTileFit} />)}
                {screens.map((screen) => <Tile key={screen.screenId} id={screen.session_id} isScreenShare />)}
              </>
            ) : (
              <Box className="info-box">
                <h1>Waiting for others</h1>
                <p>Invite someone by sharing this link:</p>
                <span className="room-url">{window.location.href}</span>
              </Box>
            )}
            {/* Always mounted: live regions are only announced reliably when the node exists before its text changes.
                Screen readers hear finished, full sentences only; announcing every streamed word would stutter. */}
            <Box
              role="status"
              aria-live="polite"
              sx={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap' }}
            >
              {captionsEnabled && !isAlone && caption?.final
                ? `${caption.role === 'user' ? 'You' : speakerName}: ${caption.text}`
                : ''}
            </Box>
            {captionsEnabled && caption && !isAlone && (
              <Box
                className="closed-caption"
                aria-hidden="true"
                sx={{
                  position: 'absolute',
                  left: '16px',
                  right: '16px',
                  // Just above the tray (top ~98px) so it sits on the chest/shoulders, not the avatar's face.
                  bottom: '110px',
                  display: 'flex',
                  justifyContent: 'center',
                  pointerEvents: 'none',
                  // Above tiles, self-view (10) and name labels (20).
                  zIndex: 30,
                }}
              >
                <Box
                  ref={steadyCaptions ? captionBoxRef : undefined}
                  sx={{
                    // Centered. On desktop keep the pill out of the bottom-right self-view (300px + 20px margin,
                    // 100px minimized) while it is wide enough; never wider than the pane (split layouts are narrow).
                    // Below ~1050px it can still overlap the self-view corner. Steady captions use a fixed width
                    // and left-aligned lines so the box and the words stay still while the caption grows.
                    [steadyCaptions ? 'width' : 'maxWidth']: isMobileLayout
                      ? '100%'
                      : isMinimized
                        ? 'min(720px, calc(100% - 280px))'
                        : 'min(100%, max(360px, min(720px, calc(100% - 680px))))',
                    padding: '0.4rem 0.9rem',
                    borderRadius: '6px',
                    background: 'rgba(0, 0, 0, 0.75)',
                    color: '#fff',
                    fontSize: '1.1rem',
                    textAlign: steadyCaptions ? 'left' : 'center',
                    overflowWrap: 'anywhere',
                    ...(steadyCaptions ? { boxSizing: 'border-box' } : {}),
                  }}
                >
                  {steadyCaptions ? (
                    <>
                      <Box component="span" sx={{ display: 'block', opacity: 0.7, fontWeight: 600, fontSize: '0.9rem' }}>
                        {caption.role === 'user' ? 'You:' : `${speakerName}:`}
                      </Box>
                      {captionPage(caption.text, captionFit ?? undefined).map((line, i) => (
                        <Box key={i} component="span" sx={{ display: 'block' }}>{line}</Box>
                      ))}
                    </>
                  ) : (
                    <>
                      <Box component="span" sx={{ opacity: 0.7, fontWeight: 600, marginRight: '0.4em' }}>
                        {caption.role === 'user' ? 'You:' : `${speakerName}:`}
                      </Box>
                      {tailCaption(caption.text)}
                    </>
                  )}
                </Box>
              </Box>
            )}
            <Tray
              leaveCall={leaveCall}
              toggleTranscript={toggleTranscript}
              isTranscriptVisible={isTranscriptOpen}
              captionsEnabled={captionsEnabled}
              toggleCaptions={toggleCaptions}
            />
          </Box>

          {!isTranscriptOpen && !isMobileLayout && (
            <Tooltip title="Open transcript & feedback" placement="left">
            <IconButton
              aria-label="Open transcript and feedback"
              onClick={() => setTranscriptVisibility(true)}
              sx={{
                position: 'absolute',
                top: '50%',
                right: '10px',
                transform: 'translateY(-50%)',
                zIndex: 10,
                color: 'white',
                backgroundColor: 'rgba(0,0,0,0.5)',
                '&:hover': {
                  backgroundColor: 'rgba(0,0,0,0.7)',
                },
              }}
            >
              <ChevronLeftIcon />
            </IconButton>
            </Tooltip>
          )}

          {layoutMode === 'standalone' && isTranscriptOpen && !isMobileLayout && (
            <Box
              className="resize-handle"
              onMouseDown={handleMouseDown}
              sx={{
                width: '6px',
                cursor: 'col-resize',
                position: 'absolute',
                top: '16px',
                bottom: '16px',
                left: `calc(100% - ${(draggedWidth !== null ? draggedWidth : transcriptWidth)}px - 35px)`,
                zIndex: 100,
                backgroundColor: '#d0d0d0',
                borderRadius: '3.5px',
                transition: isResizing ? 'none' : 'background-color 0.2s',
                '&:hover': { backgroundColor: '#707070' }
              }}
            />
          )}

          <Box
            className={`transcript-container${isTranscriptOpen ? ' expanded' : ' collapsed'}`}
            sx={{
              height: isMobileLayout
                ? isTranscriptOpen ? (isWorkspaceLayout ? '100%' : '100vh') : '0'
                : isWorkspaceLayout ? '100%' : 'calc(100vh - 96px)',
              my: isMobileLayout || isWorkspaceLayout ? '0' : '16px',
              mr: isMobileLayout || isWorkspaceLayout ? '0' : '16px',
              mb: isMobileLayout ? '0' : undefined,
              width: isMobileLayout
                ? '100%'
                : isWorkspaceLayout
                  ? undefined
                  : isTranscriptOpen ? `${(draggedWidth !== null ? draggedWidth : transcriptWidth)}px` : '0',
              transition: isWorkspaceLayout || isResizing ? 'none' : 'width 0.3s ease-in-out, height 0.3s ease-in-out',
              display: 'flex',
              flexDirection: 'column',
              position: isMobileLayout && isTranscriptOpen ? (isWorkspaceLayout ? 'absolute' : 'fixed') : undefined,
              top: isMobileLayout && isTranscriptOpen ? 0 : undefined,
              left: isMobileLayout && isTranscriptOpen ? 0 : undefined,
              zIndex: isMobileLayout && isTranscriptOpen ? 120 : undefined,
            }}
          >
            <Box
              component="aside"
              className="transcript-panel"
              sx={{
                flex: 1,
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                borderRadius: 'var(--radius-lg)',
                background: 'var(--transcript-bg)',
                backdropFilter: 'blur(20px)',
                border: '1px solid var(--border-color)',
                boxShadow: 'var(--shadow-lg)',
              }}
            >
              <Box
                className="transcript-header"
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: 'var(--space-md) var(--space-lg)',
                  borderBottom: '1px solid var(--border-color)',
                  background: 'var(--header-bg)',
                }}
              >
                <h2 className="transcript-title" style={{ margin: 0, fontSize: '1.1rem', color: 'var(--text-primary)' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Button
                      size="small"
                      onClick={() => onSelectTab('transcript')}
                      sx={{
                        minWidth: 'auto',
                        px: 1.25,
                        color: activeTab === 'transcript' ? 'var(--color-accent)' : 'var(--text-secondary)',
                        background: activeTab === 'transcript' ? 'var(--color-accent-soft)' : 'transparent',
                        '&:hover': { background: 'var(--color-accent-soft)' }
                      }}
                    >
                      Transcript
                    </Button>
                    <Button
                      size="small"
                      onClick={() => onSelectTab('events')}
                      sx={{
                        minWidth: 'auto',
                        px: 1.25,
                        color: activeTab === 'events' ? 'var(--color-accent)' : 'var(--text-secondary)',
                        background: activeTab === 'events' ? 'var(--color-accent-soft)' : 'transparent',
                        '&:hover': { background: 'var(--color-accent-soft)' }
                      }}
                    >
                      Events{tavusEvents.length > 0 ? ` (${tavusEvents.length})` : ''}
                    </Button>
                  </Box>
                </h2>
                <Box className="transcript-header-controls" sx={{ display: 'flex', gap: 1 }}>
                  {!isMobileLayout && activeTab === 'transcript' && (
                    <IconButton
                      size="small"
                      className={`transcript-toggle-badges ${showClassification ? 'active' : ''}`}
                      onClick={() => setShowClassification(!showClassification)}
                      title="Toggle Classification Badges"
                      sx={{
                        color: showClassification ? 'var(--color-accent)' : 'var(--text-secondary)',
                        background: showClassification ? 'var(--color-accent-soft)' : 'transparent'
                      }}
                    >
                      <ClassifyIcon fontSize="small" />
                    </IconButton>
                  )}
                  <Tooltip title="Close Transcript">
                    <IconButton
                      size="small"
                      onClick={() => setTranscriptVisibility(false)}
                      sx={{ color: 'var(--text-secondary)' }}
                    >
                      {isMobileLayout ? <CloseIcon /> : <ChevronRightIcon />}
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>

              {activeTab === 'transcript' ? (
                <Box ref={transcriptRef} sx={{ flex: 1, overflowY: 'auto', padding: 'var(--space-md)' }}>
                  {conversationError && <p className="conversation-error">{conversationError}</p>}
                  {conversationEntries.length === 0 && !openingPreamble && isConversationLoading ? (
                    <p className="transcript-empty">Loading conversation…</p>
                  ) : conversationEntries.length === 0 && !openingPreamble ? (
                    <p className="transcript-empty">No conversation yet.</p>
                  ) : (
                    <Box component="ul" className="transcript-list" sx={{ listStyle: 'none', p: 0, m: 0 }}>
                      {openingPreamble && (
                        <li key="opening-preamble" className="transcript-item">
                          <div className="transcript-row persona-row">
                            <div className="transcript-avatar">
                              <Avatar
                                src={persona?.imageURL || persona?.avatar_url}
                                alt={persona?.name}
                                sx={{
                                  width: 36,
                                  height: 36,
                                  bgcolor: 'var(--color-accent)',
                                  fontSize: '1rem',
                                  fontWeight: 'bold',
                                  border: '1.5px solid var(--color-accent-soft)'
                                }}
                              >
                                {persona?.name?.charAt(0).toUpperCase() || 'A'}
                              </Avatar>
                            </div>
                            <div className="transcript-text">
                              <span className="transcript-label">{speakerName}</span>
                              <MarkdownContent content={stripExtraTags(openingPreamble)} />
                            </div>
                          </div>
                        </li>
                      )}
                      {conversationEntries.map((exchange) => (
                        <li key={exchange.exchangeId} className="transcript-item">
                          <div className="transcript-row user-row">
                            <div className="transcript-avatar">
                              <Avatar
                                sx={{
                                  width: 36,
                                  height: 36,
                                  bgcolor: 'var(--color-sage)',
                                  fontSize: '1rem',
                                  fontWeight: 'bold'
                                }}
                              >
                                {userInfo?.initial}
                              </Avatar>
                            </div>
                            <div className="transcript-text">
                              <p style={{ color: 'var(--text-primary)' }}>{stripExtraTags(exchange.question || '—')}</p>
                              {showClassification && exchange.safetyRoute && (
                                <Box sx={{ mt: 0.5, display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
                                  <ClassificationBadge safetyRoute={exchange.safetyRoute} compact />
                                </Box>
                              )}
                            </div>
                          </div>

                          <div className="transcript-row persona-row">
                            <div className="transcript-avatar">
                              <Avatar
                                src={persona?.imageURL || persona?.avatar_url}
                                alt={persona?.name}
                                sx={{
                                  width: 36,
                                  height: 36,
                                  bgcolor: 'var(--color-accent)',
                                  fontSize: '1rem',
                                  fontWeight: 'bold',
                                  border: '1.5px solid var(--color-accent-soft)'
                                }}
                              >
                                {persona?.name?.charAt(0).toUpperCase() || 'A'}
                              </Avatar>
                            </div>
                            <div className="transcript-text">
                              <span className="transcript-label">{speakerName}</span>
                              <MarkdownContent
                                content={stripExtraTags(exchange.response)}
                                sources={exchange.sources}
                              />

                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, justifyContent: 'flex-end', width: '100%' }}>
                                <FeedbackButtons
                                  exchangeId={exchange.exchangeId}
                                  mode="AVATAR"
                                  initialCategories={exchange.feedbackCategory}
                                  initialComment={exchange.comment}
                                  onViewGraph={(seeds) => {
                                    setGraphModalSeeds(seeds);
                                    setGraphModalRetrieved(exchange.retrievedChunkIds || []);
                                    setGraphModalUsed(exchange.usedChunkIds || []);
                                    setGraphModalOpen(true);
                                  }}
                                  onFeedback={handleFeedback}
                                  initialRating={exchange.rating}
                                  seeds={exchange.seeds}
                                  textColor="var(--text-primary)"
                                  iconColor="var(--text-secondary)"
                                />
                              </Box>
                            </div>
                          </div>
                        </li>
                      ))}
                    </Box>
                  )}
                </Box>
              ) : (
                <Box ref={eventsRef} sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', padding: 'var(--space-md)', minWidth: 0 }}>
                  {!eventsEnabled ? (
                    <p className="transcript-empty">Enable Events to start capturing Tavus CVI app-message events.</p>
                  ) : tavusEvents.length === 0 ? (
                    <p className="transcript-empty">No events yet. Start speaking or interacting to generate CVI events.</p>
                  ) : (
                    <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0, display: 'flex', flexDirection: 'column', gap: 1.5, minWidth: 0 }}>
                      {tavusEvents.map((evt) => {
                        const dataAny: any = evt.data as any;
                        const eventType = dataAny?.event_type || dataAny?.eventType || dataAny?.type || 'app-message';
                        const displayPayload = formatTavusEventForDisplay(evt.data);
                        const ts = new Date(evt.receivedAtMs).toLocaleTimeString();
                        return (
                          <li
                            key={evt.id}
                            style={{
                              border: isMobileLayout
                                ? '1px solid var(--events-border-color)'
                                : '1px solid var(--border-color)',
                              borderRadius: 10,
                              padding: 10,
                            }}
                          >
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                              <span style={{ fontWeight: 600, color: 'var(--text-primary)', overflowWrap: 'break-word', wordBreak: 'break-word' }}>{eventType}</span>
                              <span style={{ fontSize: 12, color: isMobileLayout ? 'var(--text-primary)' : 'var(--text-secondary)' }}>
                                {ts}{evt.fromId ? ` · from ${evt.fromId}` : ''}
                              </span>
                            </Box>
                            <pre style={{
                              margin: '8px 0 0 0',
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word',
                              overflowWrap: 'break-word',
                              fontSize: 12,
                              color: isMobileLayout
                                ? (isDarkMode ? 'var(--text-secondary)' : 'var(--text-primary)')
                                : 'var(--text-secondary)',
                            }}>
                              {JSON.stringify(displayPayload, null, 2)}
                            </pre>
                          </li>
                        );
                      })}
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          </Box>
        </Box>

        <SubgraphModal
          open={graphModalOpen}
          onClose={() => setGraphModalOpen(false)}
          seedIds={graphModalSeeds}
          retrievedIds={graphModalRetrieved}
          usedIds={graphModalUsed}
          tenantId={persona?.tenantId || 'chip'}
          title="Knowledge Graph for This Response"
        />

        <DocxViewer url={url} highlightText={highlightText} source={source}/>

      </>
    </citationViewerOpenContext.Provider>
    </sourceContext.Provider>
    </highlightTextContext.Provider>
    </urlContext.Provider>
    );
  };

  return getUserMediaError ? <UserMediaError /> : renderCallScreen();
}
